/**
 * ChecklistReviewSheet — Read-only admin/ops review of the installer checklist
 * for a single job. Opens as a right-side drawer from Install Today rows so
 * the user can answer "what was actually done?" without navigating away into
 * the installer execution flow.
 *
 * Read-only. No writes. No state mutations. Pure derivation over
 * `specExecutions` + the job's bound JobSpec.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Circle, Camera, AlertTriangle, Clock, ShieldCheck,
  ArrowUpRight, ClipboardList,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAppData } from "@/data/AppDataContext";
import { resolveBoundSpec } from "@/data/jobSpec";
import type { PhaseExecution } from "@/data/jobSpecExecution";
import { cn } from "@/lib/utils";

interface Props {
  jobId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function fmtTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function phaseLabel(status: PhaseExecution["status"]) {
  switch (status) {
    case "qa_passed":
      return <Badge className="bg-success/15 text-success ring-1 ring-success/30 gap-1"><ShieldCheck className="h-3 w-3" /> QA Passed</Badge>;
    case "submitted":
      return <Badge className="bg-primary/15 text-primary ring-1 ring-primary/30 gap-1"><Clock className="h-3 w-3" /> Awaiting QA</Badge>;
    case "qa_failed":
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> QA Failed</Badge>;
    case "in_progress":
      return <Badge variant="outline">In progress</Badge>;
    default:
      return <Badge variant="outline" className="text-muted-foreground">Not started</Badge>;
  }
}

export function ChecklistReviewSheet({ jobId, open, onOpenChange }: Props) {
  const { jobs, specExecutions } = useAppData();

  const job = useMemo(
    () => (jobId ? jobs.find((j) => j.id === jobId) : undefined),
    [jobs, jobId],
  );

  const spec = useMemo(
    () => (job?.boundJobSpec ? resolveBoundSpec(job.boundJobSpec) : undefined),
    [job],
  );

  const execution = useMemo(
    () => (jobId ? specExecutions.find((e) => e.jobId === jobId) : undefined),
    [specExecutions, jobId],
  );

  // Totals across all phases.
  const totals = useMemo(() => {
    if (!spec || !execution) return null;
    let checked = 0, total = 0, photos = 0, photosReq = 0;
    for (const sp of spec.phases) {
      const pe = execution.phases.find((p) => p.phaseName === sp.name);
      total += sp.checklistItems.length;
      photosReq += sp.requiredPhotos.length;
      if (pe) {
        checked += pe.checklist.filter((c) => c.checked).length;
        photos += pe.photos.length;
      }
    }
    return { checked, total, photos, photosReq };
  }, [spec, execution]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="space-y-1">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
            <SheetTitle className="text-base">Install Checklist</SheetTitle>
          </div>
          {job && (
            <SheetDescription className="text-xs">
              {job.customerName} · {job.id}
            </SheetDescription>
          )}
        </SheetHeader>

        {!job ? (
          <div className="mt-6 text-sm text-muted-foreground">Job not found.</div>
        ) : !spec ? (
          <div className="mt-6 text-sm text-muted-foreground">
            This job has no JobSpec bound, so there's no checklist to review yet.
          </div>
        ) : !execution || totals === null ? (
          <div className="mt-6 text-sm text-muted-foreground">
            The installer hasn't started this checklist yet. Nothing to show.
          </div>
        ) : (
          <>
            {/* Roll-up */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-md border p-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Checklist</p>
                <p className="text-lg font-semibold tabular-nums">
                  {totals.checked}<span className="text-muted-foreground font-normal">/{totals.total}</span>
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Photos</p>
                <p className="text-lg font-semibold tabular-nums">
                  {totals.photos}<span className="text-muted-foreground font-normal">/{totals.photosReq}</span>
                </p>
              </div>
            </div>

            {/* Per-phase */}
            <div className="mt-5 space-y-4">
              {spec.phases.map((sp) => {
                const pe = execution.phases.find((p) => p.phaseName === sp.name);
                const phaseChecked = pe?.checklist.filter((c) => c.checked).length ?? 0;
                const phasePhotos = pe?.photos.length ?? 0;
                return (
                  <div key={sp.name} className="rounded-lg border bg-card">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/30">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold capitalize">{sp.label || sp.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {phaseChecked}/{sp.checklistItems.length} checks · {phasePhotos}/{sp.requiredPhotos.length} photos
                          {pe?.completedAt ? ` · submitted ${fmtTime(pe.completedAt)}` : ""}
                          {pe?.completedBy ? ` by ${pe.completedBy}` : ""}
                        </p>
                      </div>
                      {phaseLabel(pe?.status ?? "not_started")}
                    </div>

                    {/* Checklist items */}
                    <ul className="divide-y">
                      {sp.checklistItems.map((item) => {
                        const resp = pe?.checklist.find((c) => c.itemId === item.id);
                        const done = !!resp?.checked;
                        return (
                          <li key={item.id} className="flex items-start gap-2.5 px-3 py-2">
                            {done ? (
                              <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
                            ) : (
                              <Circle className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className={cn("text-sm leading-snug", done ? "text-foreground" : "text-muted-foreground")}>
                                {item.label}
                              </p>
                              {done && resp?.checkedAt && (
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  Checked {fmtTime(resp.checkedAt)}
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                      {sp.checklistItems.length === 0 && (
                        <li className="px-3 py-2 text-xs text-muted-foreground italic">
                          No checklist items for this phase.
                        </li>
                      )}
                    </ul>

                    {/* QA fail reason (if any) */}
                    {pe?.status === "qa_failed" && pe.qaFailReason && (
                      <div className="px-3 py-2 border-t bg-destructive/5 text-xs text-destructive flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span><span className="font-medium">QA failed:</span> {pe.qaFailReason}</span>
                      </div>
                    )}

                    {/* Photo progress (compact) */}
                    {sp.requiredPhotos.length > 0 && (
                      <div className="px-3 py-2 border-t flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Camera className="h-3 w-3" />
                        <span>{phasePhotos} of {sp.requiredPhotos.length} required photos uploaded</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <Separator className="my-5" />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-[11px] text-muted-foreground">
                Read-only review. To edit phases, open the installer execution view.
              </p>
              <Link
                to={`/installer-execution/${job.id}`}
                onClick={() => onOpenChange(false)}
              >
                <Button variant="outline" size="sm" className="gap-1.5">
                  Open installer view <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
